/**
 * ChurchesMap - Interactive map view of churches with geographic filtering
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import { 
  Map, Church, Search, Filter, X, Globe, MapPin, Phone, 
  ExternalLink, List, LayoutGrid, ChevronRight, Loader2
} from 'lucide-react';
import api from '@/lib/api';

// Import leaflet CSS
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom church marker icon
const churchIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#4F46E5" stroke-width="2">
      <path d="M18 22H6V11l6-7 6 7v11z"/>
      <path d="M12 4V1"/>
      <path d="M10 1h4"/>
      <path d="M10 22v-4h4v4"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Map bounds adjuster component
const MapBoundsAdjuster = ({ churches }) => {
  const map = useMap();

  useEffect(() => {
    if (churches && churches.length > 0) {
      const validChurches = churches.filter(c => c.latitude && c.longitude);
      if (validChurches.length > 0) {
        const bounds = L.latLngBounds(
          validChurches.map(c => [c.latitude, c.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [churches, map]);

  return null;
};

// Continent coordinates for initial views
const CONTINENT_COORDS = {
  'Africa': { lat: 0, lng: 20, zoom: 3 },
  'Asia': { lat: 34, lng: 100, zoom: 3 },
  'Europe': { lat: 50, lng: 10, zoom: 4 },
  'North America': { lat: 40, lng: -100, zoom: 3 },
  'South America': { lat: -15, lng: -60, zoom: 3 },
  'Oceania': { lat: -25, lng: 135, zoom: 3 },
  'Antarctica': { lat: -80, lng: 0, zoom: 2 },
};

const ChurchesMap = () => {
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'list' | 'grid'
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    continent: '',
    country: '',
    region: '',
    city: ''
  });
  const [selectedChurch, setSelectedChurch] = useState(null);

  // Fetch locations for filters
  const { data: locations } = useQuery({
    queryKey: ['church-locations'],
    queryFn: () => api.get('/admin/churches/locations').then(r => r.data)
  });

  // Fetch churches
  const { data: churches, isLoading } = useQuery({
    queryKey: ['churches-map', filters, searchQuery],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.continent) params.append('continent', filters.continent);
      if (filters.country) params.append('country', filters.country);
      if (filters.region) params.append('region', filters.region);
      if (filters.city) params.append('city', filters.city);
      if (searchQuery) params.append('search', searchQuery);
      return api.get(`/admin/churches?${params.toString()}`).then(r => r.data);
    }
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['church-stats'],
    queryFn: () => api.get('/admin/churches/stats').then(r => r.data)
  });

  // Filter cascading options
  const filteredCountries = useMemo(() => {
    if (!locations?.countries) return [];
    if (!filters.continent) return locations.countries;
    return locations.countries.filter(c => 
      c.continent?.toLowerCase() === filters.continent.toLowerCase()
    );
  }, [locations, filters.continent]);

  const filteredRegions = useMemo(() => {
    if (!locations?.regions) return [];
    if (!filters.country) return locations.regions;
    return locations.regions.filter(r => 
      r.country?.toLowerCase() === filters.country.toLowerCase()
    );
  }, [locations, filters.country]);

  const filteredCities = useMemo(() => {
    if (!locations?.cities) return [];
    let result = locations.cities;
    if (filters.country) {
      result = result.filter(c => c.country?.toLowerCase() === filters.country.toLowerCase());
    }
    if (filters.region) {
      result = result.filter(c => c.region?.toLowerCase() === filters.region.toLowerCase());
    }
    return result;
  }, [locations, filters.country, filters.region]);

  // Churches with valid coordinates
  const churchesWithCoords = useMemo(() => {
    return (churches || []).filter(c => c.latitude && c.longitude);
  }, [churches]);

  const clearFilters = () => {
    setFilters({ continent: '', country: '', region: '', city: '' });
    setSearchQuery('');
  };

  const hasActiveFilters = filters.continent || filters.country || filters.region || filters.city || searchQuery;

  // Get initial map center
  const mapCenter = useMemo(() => {
    if (filters.continent && CONTINENT_COORDS[filters.continent]) {
      return [CONTINENT_COORDS[filters.continent].lat, CONTINENT_COORDS[filters.continent].lng];
    }
    return [20, 0]; // World center
  }, [filters.continent]);

  return (
    <div className="space-y-4" data-testid="churches-map">
      {/* Header with Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Map className="h-6 w-6 text-indigo-600" />
            Churches Directory
          </h2>
          <p className="text-sm text-slate-500">
            {stats?.total || 0} churches • {stats?.with_coordinates || 0} mapped
          </p>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'map' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('map')}
          >
            <Map className="h-4 w-4 mr-1" />
            Map
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4 mr-1" />
            Grid
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search churches..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Continent Filter */}
            <Select
              value={filters.continent || "all"}
              onValueChange={(v) => setFilters({ ...filters, continent: v === "all" ? '' : v, country: '', region: '', city: '' })}
            >
              <SelectTrigger className="w-full max-w-[150px]">
                <Globe className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Continent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Continents</SelectItem>
                {(locations?.continents || []).map(c => (
                  <SelectItem key={c.name} value={c.name}>
                    {c.name} ({c.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Country Filter */}
            <Select
              value={filters.country || "all"}
              onValueChange={(v) => setFilters({ ...filters, country: v === "all" ? '' : v, region: '', city: '' })}
            >
              <SelectTrigger className="w-full max-w-[150px]">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {filteredCountries.map(c => (
                  <SelectItem key={c.name} value={c.name}>
                    {c.name} ({c.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Region Filter */}
            <Select
              value={filters.region || "all"}
              onValueChange={(v) => setFilters({ ...filters, region: v === "all" ? '' : v, city: '' })}
            >
              <SelectTrigger className="w-full max-w-[150px]">
                <SelectValue placeholder="Region/State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {filteredRegions.map(r => (
                  <SelectItem key={r.name} value={r.name}>
                    {r.name} ({r.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* City Filter */}
            <Select
              value={filters.city || "all"}
              onValueChange={(v) => setFilters({ ...filters, city: v === "all" ? '' : v })}
            >
              <SelectTrigger className="w-full max-w-[150px]">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {filteredCities.map(c => (
                  <SelectItem key={c.name} value={c.name}>
                    {c.name} ({c.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Active Filters Breadcrumb */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-3 text-sm text-slate-600">
              <Filter className="h-4 w-4" />
              {filters.continent && (
                <>
                  <Badge variant="secondary">{filters.continent}</Badge>
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
              {filters.country && (
                <>
                  <Badge variant="secondary">{filters.country}</Badge>
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
              {filters.region && (
                <>
                  <Badge variant="secondary">{filters.region}</Badge>
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
              {filters.city && (
                <Badge variant="secondary">{filters.city}</Badge>
              )}
              <span className="text-slate-400 ml-2">
                {churches?.length || 0} results
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-[500px] bg-slate-100 rounded-lg">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : viewMode === 'map' ? (
        /* Map View */
        <Card className="overflow-hidden">
          <div className="h-[500px] relative">
            <MapContainer
              center={mapCenter}
              zoom={2}
              className="h-full w-full"
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              <MapBoundsAdjuster churches={churchesWithCoords} />
              
              {churchesWithCoords.map((church) => (
                <Marker
                  key={church.id}
                  position={[church.latitude, church.longitude]}
                  icon={churchIcon}
                  eventHandlers={{
                    click: () => setSelectedChurch(church)
                  }}
                >
                  <Popup>
                    <div className="min-w-0 sm:min-w-[200px]">
                      <h3 className="font-semibold">{church.name}</h3>
                      <p className="text-sm text-slate-600">
                        {[church.city, church.region || church.state, church.country]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                      {church.address && (
                        <p className="text-xs text-slate-500 mt-1">{church.address}</p>
                      )}
                      {church.contact_phone && (
                        <p className="text-xs text-slate-500">
                          <Phone className="h-3 w-3 inline mr-1" />
                          {church.contact_phone}
                        </p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* No coordinates notice */}
            {churches?.length > 0 && churchesWithCoords.length === 0 && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-600">No churches with coordinates found</p>
                  <p className="text-sm text-slate-500">Add latitude/longitude to churches to show them on the map</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      ) : viewMode === 'list' ? (
        /* List View */
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {(churches || []).map((church) => (
                <div
                  key={church.id}
                  className="p-4 hover:bg-slate-50 cursor-pointer flex items-center gap-4"
                  onClick={() => setSelectedChurch(church)}
                >
                  {church.logo_url ? (
                    <img
                      src={church.logo_url}
                      alt=""
                      className="h-12 w-12 object-contain rounded"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-indigo-100 rounded flex items-center justify-center">
                      <Church className="h-6 w-6 text-indigo-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{church.name}</h3>
                    <p className="text-sm text-slate-500 truncate">
                      {[church.city, church.region || church.state, church.country]
                        .filter(Boolean)
                        .join(', ') || 'No location'}
                    </p>
                  </div>
                  {church.latitude && church.longitude && (
                    <Badge variant="secondary" className="shrink-0">
                      <MapPin className="h-3 w-3 mr-1" />
                      Mapped
                    </Badge>
                  )}
                </div>
              ))}
              {(!churches || churches.length === 0) && (
                <div className="p-8 text-center text-slate-500">
                  <Church className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                  <p>No churches found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(churches || []).map((church) => (
            <Card
              key={church.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedChurch(church)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {church.logo_url ? (
                    <img
                      src={church.logo_url}
                      alt=""
                      className="h-14 w-14 object-contain rounded"
                    />
                  ) : (
                    <div className="h-14 w-14 bg-indigo-100 rounded flex items-center justify-center shrink-0">
                      <Church className="h-7 w-7 text-indigo-600" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-medium truncate">{church.name}</h3>
                    <p className="text-sm text-slate-500 truncate">
                      {church.city || church.country || 'No location'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      {church.continent && (
                        <Badge variant="outline" className="text-xs">{church.continent}</Badge>
                      )}
                      {church.latitude && church.longitude && (
                        <Badge variant="secondary" className="text-xs">
                          <MapPin className="h-3 w-3" />
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {(!churches || churches.length === 0) && (
            <div className="col-span-full p-8 text-center text-slate-500 bg-slate-50 rounded-lg">
              <Church className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No churches found</p>
            </div>
          )}
        </div>
      )}

      {/* Stats by Region */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">By Continent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(stats.by_continent || []).map((c) => (
                  <div key={c.name} className="flex items-center justify-between">
                    <span className="text-sm">{c.name}</span>
                    <Badge variant="secondary">{c.count}</Badge>
                  </div>
                ))}
                {(!stats.by_continent || stats.by_continent.length === 0) && (
                  <p className="text-sm text-slate-400">No continent data</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Top Countries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(stats.by_country || []).slice(0, 8).map((c) => (
                  <div key={c.name} className="flex items-center justify-between">
                    <span className="text-sm truncate">{c.name}</span>
                    <Badge variant="secondary">{c.count}</Badge>
                  </div>
                ))}
                {(!stats.by_country || stats.by_country.length === 0) && (
                  <p className="text-sm text-slate-400">No country data</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ChurchesMap;
